/**
 * Shared formatters for the session list/detail "duration · slots" subline.
 *
 * Single source of truth so the tenant list rows and the detail header read
 * identically (e.g. "2h · 6 courts", "1h 20m · 1 court", "40m · 2 courts").
 */
/**
 * Format a millisecond duration as a compact "2h", "1h 20m", or "40m" string.
 * Negative or zero durations collapse to "0m".
 */
export function formatDuration(startIso, endIso) {
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    const totalMin = Math.max(0, Math.round((end - start) / 60000));
    const hours = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    if (hours > 0 && mins > 0)
        return `${hours}h ${mins}m`;
    if (hours > 0)
        return `${hours}h`;
    return `${mins}m`;
}
/** Pluralised court/slot count, e.g. "1 court" / "6 courts". */
export function formatCourtCount(total) {
    return `${total} court${total === 1 ? "" : "s"}`;
}
/** Add `mins` minutes to a "HH:MM" clock string, returning "HH:MM". */
export function addMinutes(hhmm, mins) {
    const [h, m] = hhmm.split(":").map(Number);
    const total = h * 60 + m + mins;
    const eh = Math.floor(total / 60);
    const em = total % 60;
    return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
}
/** "18:00 – 20:00" from two ISO instants (local time, 24h). */
export function formatTimeRange(startIso, endIso) {
    const fmt = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return `${fmt(new Date(startIso))} – ${fmt(new Date(endIso))}`;
}
/** Compact "5 Jun, 14:30" timestamp for log/audit rows (local time). */
export function formatTimestamp(iso) {
    return new Date(iso).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}
/**
 * Combined "duration · N courts" subline. `endIso` is optional — when missing
 * only the court count is shown (older list payloads may omit it).
 */
export function formatSessionSubline(startIso, endIso, total) {
    const courts = formatCourtCount(total);
    if (!endIso)
        return courts;
    return `${formatDuration(startIso, endIso)} · ${courts}`;
}
